import { useApolloClient, useMutation } from '@apollo/client';
import { FieldInput } from 'aimo-ui';
import StepWrapper from 'components/common/StepWrapper';
import SuccessIndicator from 'components/common/SuccessIndicator';
import { UPDATE_RFID_TAG_NAME } from 'graphql/mutations/updateRfidTagName';
import { GET_CUSTOMER_RFID_TOKENS } from 'graphql/queries/getCustomerRfidTokens';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useRfidStore } from 'stores/rfidStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  Mutation,
  MutationUpdateRfidTagNameArgs,
} from 'types/generatedSchemaTypes';
import { isIos } from 'utils/deviceUtils';
import { useUiStore } from 'stores/uiStore';

const RfidScanCompleteModal: React.FC = () => {
  const { t } = useTranslation();
  const [updateTagNameStatus, setUpdateTagNameStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const tagName = useRfidStore((state) => state.currentTagName);
  const setCurrentTagName = useRfidStore((state) => state.setCurrentTagName);
  const serialNumber = useRfidStore(
    (state) => state.rfIdRegistrationSerialNumber
  );
  const client = useApolloClient();

  useEffect(() => {
    useRfidStore.getState().setCurrentTagName('');
  }, []);

  useEffect(() => {
    if (serialNumber) {
      const tagNumber = Date.now().toString().slice(-4);
      useRfidStore.getState().setCurrentTagName(`My-tag-${tagNumber}`);
    }
  }, [serialNumber]);

  const [updateRfidTagName] = useMutation<
    Mutation,
    MutationUpdateRfidTagNameArgs
  >(UPDATE_RFID_TAG_NAME, {
    onCompleted: () => {
      setUpdateTagNameStatus('success');
      useRfidStore.getState().setRfIdRegistrationSerialNumber('');
      client.refetchQueries({
        include: [GET_CUSTOMER_RFID_TOKENS],
      });
      useUiStore.getState().setSheetModal('');
    },
    onError: () => {
      setUpdateTagNameStatus('error');
    },
  });

  const handleSaveTagName = () => {
    if (!tagName.trim()) return;

    setUpdateTagNameStatus('loading');
    updateRfidTagName({
      variables: {
        input: {
          serialNumber,
          name: tagName.trim(),
        },
      },
    });
  };

  const renderTagSuccess = () => (
    <StepWrapper
      progress={100}
      description={t(
        'account.tagsAndCards.registerRfid.tagSuccess.description'
      )}
      primaryButton={{
        text:
          updateTagNameStatus === 'loading'
            ? t('account.tagsAndCards.registerRfid.tagSuccess.saving')
            : t('account.tagsAndCards.registerRfid.tagSuccess.save'),
        disabled: !tagName.trim() || updateTagNameStatus === 'loading',
        loading: updateTagNameStatus === 'loading',
        onPress: handleSaveTagName,
        testID: 'save-button',
      }}
      errorMessage={
        updateTagNameStatus === 'error'
          ? t('account.tagsAndCards.registerRfid.tagSuccess.saveFailed')
          : undefined
      }>
      <SuccessIndicator
        message={t(
          'account.tagsAndCards.registerRfid.tagSuccess.successMessage'
        )}
        testID="tag-register-success"
      />

      <StyledWrapper mb={30}>
        <FieldInput
          testID="tag-name-input"
          title={t('account.tagsAndCards.registerRfid.tagSuccess.tagName')}
          value={tagName}
          isSheet={isIos}
          onChangeText={setCurrentTagName}
          placeholder={t(
            'account.tagsAndCards.registerRfid.tagSuccess.placeholder'
          )}
        />
      </StyledWrapper>
    </StepWrapper>
  );

  return <View>{renderTagSuccess()}</View>;
};

export default RfidScanCompleteModal;
